import { createCognitoAuthorizationHeader } from "./../utils/createCognitoAuthorizationHeader";
import { CognitoUser } from "amazon-cognito-identity-js";
import { addUserTokensToStorage } from "../redux/actions/actionCreators";
import { ApiError, TimeoutError } from "../utils/error";
import { DataDogLogger } from "../utils/logger";
import { v4 as uuidv4 } from "uuid";
import fetchJsonp from "fetch-jsonp";
const retry = require("async-retry");

const HTTP_RETRIES = 3;
const HTTP_RETRY_DELAY = 10000;
const HTTP_GATEWAY_TIMEOUT_STATUS = 504;
const CORRELATION_ID_HEADER = "x-correlation-id";

export class BaseService {
  public logger: DataDogLogger;
  constructor(
    public url: string = "",
    public cognitoUser: CognitoUser,
    public dispatch = (x: any) => {}
  ) {
    this.logger = new DataDogLogger({
      userContext: cognitoUser,
    });
  }
  createPostConfigWithoutHeaders(body: any) {
    return {
      method: "POST",
      mode: "cors",
      body: JSON.stringify(body),
    } as RequestInit;
  }
  createPatchConfigWithoutHeaders(body: any) {
    return {
      method: "PATCH",
      mode: "cors",
      body: JSON.stringify(body),
    } as RequestInit;
  }
  createDeleteConfigWithoutHeaders() {
    return {
      method: "DELETE",
      mode: "cors",
    } as RequestInit;
  }
  createPutConfigWithoutHeaders(body: any) {
    return {
      method: "PUT",
      mode: "cors",
      body: JSON.stringify(body),
    } as RequestInit;
  }
  createGetConfigWithoutHeaders() {
    return {
      method: "GET",
      mode: "cors",
    } as RequestInit;
  }

  setRefreshedTokensThunk(tokens: any) {
    return this.dispatch(addUserTokensToStorage(tokens));
  }

  customizeHeaders(headers: any): any {
    headers[CORRELATION_ID_HEADER] = uuidv4();
    return headers;
  }

  async makeGetRequest(path: string, useAccessToken = false) {
    const { headers, refreshedTokens } = await createCognitoAuthorizationHeader(
      this.cognitoUser,
      useAccessToken,
      this.dispatch
    );
    if (refreshedTokens) {
      this.setRefreshedTokensThunk(refreshedTokens);
    }
    const fetchConfig = {
      ...this.createGetConfigWithoutHeaders(),
      ...{ headers: this.customizeHeaders(headers) },
    };
    const finalUrl = `${this.url}/${path}`;
    this.logger.logRequest(finalUrl, { correlationId: fetchConfig.headers[CORRELATION_ID_HEADER] });
    const res = await fetch(finalUrl, fetchConfig);
    const json = await res.json();
    this.logger.logResponse(finalUrl, json);
    return json;
  }

  async doWithTimeoutRetries(
    request: Function,
    opts = { retries: HTTP_RETRIES, minTimeout: HTTP_RETRY_DELAY }
  ) {
    return await retry(
      async (bail: Function) => {
        try {
          return await request(this);
        } catch (error) {
          if (error instanceof TimeoutError) {
            throw error;
          }
          // if it is not timeout error - not retry; just re-throw
          bail(error);
          return;
        }
      },
      {
        retries: opts.retries,
        minTimeout: opts.minTimeout, // milliseconds
        factor: 1, // exponential factor
        randomize: false, // do not randomizes the timeouts
      }
    );
  }

  async makePostRequest(
    path: string,
    body: any,
    useAccessToken = false,
    useDefaultErrorProcesssing = true,
    signal?: AbortSignal
  ) {
    const { headers, refreshedTokens } = await createCognitoAuthorizationHeader(
      this.cognitoUser,
      useAccessToken,
      this.dispatch
    );
    if (refreshedTokens) {
      this.setRefreshedTokensThunk(refreshedTokens);
    }
    const fetchConfig = {
      ...this.createPostConfigWithoutHeaders(body),
      ...{ headers: this.customizeHeaders(headers) },
      signal: signal,
    };
    const finalUrl = `${this.url}/${path}`;
    this.logger.logRequest(finalUrl, { correlationId: fetchConfig.headers[CORRELATION_ID_HEADER] });
    const res = await fetch(finalUrl, fetchConfig);
    this.logger.logResponse(finalUrl, res);
    if (!res) {
      throw new Error("Unknown Error");
    }

    if (res.status === HTTP_GATEWAY_TIMEOUT_STATUS) {
      this.logger.warn(`Got status ${res.status} on POST request; path: ${path}`);
      throw new TimeoutError();
    }
    const json = await res.json();
    if (!res.ok) {
      if (!useDefaultErrorProcesssing && res.status < 500) {
        return json;
      }
      throw new ApiError(json.message, json.code ? json.code : null);
      // throw new Error(json.message);
    }
    return json;
  }
  async makePatchRequest(
    path: string,
    body: any,
    useAccessToken = false,
    useDefaultErrorProcesssing = true
  ) {
    const { headers, refreshedTokens } = await createCognitoAuthorizationHeader(
      this.cognitoUser,
      useAccessToken,
      this.dispatch
    );
    if (refreshedTokens) {
      this.setRefreshedTokensThunk(refreshedTokens);
    }
    const fetchConfig = {
      ...this.createPatchConfigWithoutHeaders(body),
      ...{ headers: this.customizeHeaders(headers) },
    };
    const finalUrl = `${this.url}/${path}`;
    this.logger.info(
      `Making PATCH request to ${finalUrl} with args: ${JSON.stringify(fetchConfig, null, 2)})}`
    );
    const res = await fetch(finalUrl, fetchConfig);
    this.logger.logResponse(finalUrl, res);
    if (!res) {
      throw new Error("Unknown Error");
    }

    if (res.status === HTTP_GATEWAY_TIMEOUT_STATUS) {
      this.logger.warn(`Got status ${res.status} on PATCH request; path: ${path}`);
      throw new TimeoutError();
    }
    const json = await res.json();
    if (!res.ok) {
      if (!useDefaultErrorProcesssing && res.status < 500) {
        return json;
      }
      throw new ApiError(json.message, json.code ? json.code : null);
      // throw new Error(json.message);
    }
    return json;
  }
  async makeDeleteRequest(path: string, useAccessToken = false, useDefaultErrorProcesssing = true) {
    const { headers, refreshedTokens } = await createCognitoAuthorizationHeader(
      this.cognitoUser,
      useAccessToken,
      this.dispatch
    );
    if (refreshedTokens) {
      this.setRefreshedTokensThunk(refreshedTokens);
    }
    const fetchConfig = {
      ...this.createDeleteConfigWithoutHeaders(),
      ...{ headers: this.customizeHeaders(headers) },
    };
    const finalUrl = `${this.url}/${path}`;
    this.logger.info(
      `Making DELETE request to ${finalUrl} with args: ${JSON.stringify(fetchConfig, null, 2)})}`
    );
    const res = await fetch(finalUrl, fetchConfig);
    this.logger.logResponse(finalUrl, res);
    if (!res) {
      throw new Error("Unknown Error");
    }

    if (res.status === HTTP_GATEWAY_TIMEOUT_STATUS) {
      this.logger.warn(`Got status ${res.status} on PATCH request; path: ${path}`);
      throw new TimeoutError();
    }
    const json = await res.json();
    if (!res.ok) {
      if (!useDefaultErrorProcesssing && res.status < 500) {
        return json;
      }
      throw new ApiError(json.message, json.code ? json.code : null);
      // throw new Error(json.message);
    }
    return json;
  }
  async makePutRequest(path: string, body: any, useAccessToken = false) {
    const { headers, refreshedTokens } = await createCognitoAuthorizationHeader(
      this.cognitoUser,
      useAccessToken,
      this.dispatch
    );
    if (refreshedTokens) {
      this.setRefreshedTokensThunk(refreshedTokens);
    }
    const fetchConfig = {
      ...this.createPutConfigWithoutHeaders(body),
      ...{ headers: this.customizeHeaders(headers) },
    };
    const finalUrl = `${this.url}/${path}`;
    // console.log({ finalUrl, fetchConfig });
    this.logger.logRequest(finalUrl, { correlationId: fetchConfig.headers[CORRELATION_ID_HEADER] });
    const res = await fetch(finalUrl, fetchConfig);
    const json = await res.json();
    this.logger.logResponse(finalUrl, json);
    return json;
  }

  async makePostSimpleRequest(
    path: string,
    body: any,
    contentType = "application/json",
    signal?: AbortSignal,
    useDefaultErrorProcesssing = true
  ) {
    const finalUrl = `${this.url}/${path}`;
    const response = await fetch(finalUrl, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      body,
      headers: {
        "Content-Type": contentType,
      },
      signal,
    });

    this.logger.logResponse(finalUrl, response);
    if (!response) {
      throw new Error("Unknown Error");
    }

    if (response.status === HTTP_GATEWAY_TIMEOUT_STATUS) {
      this.logger.warn(`Got status ${response.status} on POST request; path: ${path}`);
      throw new TimeoutError();
    }
    const json = await response.json();
    if (!response.ok) {
      if (!useDefaultErrorProcesssing && response.status < 500) {
        return json;
      }
      throw new ApiError(json.message, json.code ? json.code : null);
    }
    return json;
  }

  async makeGETJSONPSimpleRequest(path: string, body: any, useDefaultErrorProcesssing = true) {
    const finalUrl = `${this.url}/${path}?${body}`;

    const response = await fetchJsonp(finalUrl, {
      jsonpCallback: "jsoncallback",
      timeout: 15000,
    });

    this.logger.logResponse(finalUrl, response);
    if (!response) {
      throw new Error("Unknown Error");
    }

    if (response.status === HTTP_GATEWAY_TIMEOUT_STATUS) {
      this.logger.warn(`Got status ${response.status} on POST request; path: ${path}`);
      throw new TimeoutError();
    }
    const json = await response.json();
    if (!response.ok) {
      if (!useDefaultErrorProcesssing && response.status < 500) {
        return json;
      }
      throw new ApiError(json.message, json.code ? json.code : null);
    }
    return json;
  }
}
